import React, { useEffect, useState, useRef } from "react";
import { User, Mail, Edit3, Save, Check, X, Camera, Settings, Shield, Upload } from "lucide-react";
import UserPageHeader from '../components/UserPageHeader';
// Add AuthContext import at the top
import { AuthContext } from '../Context/AuthContext';
import { useContext } from 'react';


function UserProfile({ user, onUpdate }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedUser, setEditedUser] = useState({ ...user });
  const [saveStatus, setSaveStatus] = useState(null);
  const [profileImage, setProfileImage] = useState(null);
  const [showImageOptions, setShowImageOptions] = useState(false);
  const fileInputRef = useRef(null);

  // Initialize with stored profile image if available
  useEffect(() => {
    if (user && user.profileImage) {
      setProfileImage(user.profileImage);
    }
  }, [user]);

  if (!user) return (
    <div className="max-w-md mx-auto mt-8">
      <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl border border-white/20 p-8 text-center">
        <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
          <User className="w-10 h-10 text-gray-400" />
        </div>
        <h3 className="text-xl font-semibold text-gray-800 mb-2">No Profile Found</h3>
        <p className="text-gray-600">Please sign in to view your profile.</p>
      </div>
    </div>
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedUser(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = () => {
    const updatedUser = {
      ...editedUser,
      profileImage: profileImage // Include the profile image in the user data
    };
    onUpdate(updatedUser);
    setIsEditing(false);
    setSaveStatus('saved');
    setTimeout(() => setSaveStatus(null), 2000);
  };

  const handleCancel = () => {
    setEditedUser({ ...user });
    setIsEditing(false);
    // Revert to original profile image if editing was cancelled
    if (user.profileImage) {
      setProfileImage(user.profileImage);
    } else {
      setProfileImage(null);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setProfileImage(event.target.result);
        setShowImageOptions(false);
      };
      reader.readAsDataURL(file);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  const removeProfileImage = () => {
    setProfileImage(null);
    setShowImageOptions(false);
  };

  return (
    <div className="max-w-2xl mx-auto mt-8">
      <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl border border-white/20 overflow-hidden">
        {/* Profile Header */}
        <div className="relative bg-gradient-to-r from-blue-600 to-pink-150 px-8 py-12">
          <div className="absolute inset-0 bg-black/10"></div>
          <div className="relative flex flex-col items-center">
            <div className="relative">
              {profileImage ? (
                <div className="w-32 h-32 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center border-4 border-white/30 overflow-hidden">
                  <img 
                    src={profileImage} 
                    alt="Profile" 
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className="w-32 h-32 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center border-4 border-white/30">
                  <User className="w-16 h-16 text-white" />
                </div>
              )}
              <button 
                className="absolute bottom-2 right-2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:scale-105 transition-transform"
                onClick={() => setShowImageOptions(!showImageOptions)}
              >
                <Camera className="w-5 h-5 text-gray-600" />
              </button>

              {/* Image Options Popup */}
              {showImageOptions && (
                <div className="absolute bottom-0 right-12 w-48 bg-white rounded-lg shadow-xl p-2 z-10">
                  <button
                    onClick={triggerFileInput}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                  >
                    <Upload className="w-4 h-4" />
                    Upload Photo
                  </button>
                  {profileImage && (
                    <button
                      onClick={removeProfileImage}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md"
                    >
                      <X className="w-4 h-4" />
                      Remove Photo
                    </button>
                  )}
                  <button
                    onClick={() => setShowImageOptions(false)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-500 hover:bg-gray-100 rounded-md"
                  >
                    Cancel
                  </button>
                </div>
              )}

              {/* Hidden file input */}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageUpload}
                accept="image/*"
                className="hidden"
              />
            </div>
            <h2 className="mt-4 text-2xl font-bold text-white">{user.username || 'Unknown User'}</h2>
            <p className="text-indigo-100">{user.email || 'No email provided'}</p>
          </div>
        </div>

        {/* Profile Content */}
        <div className="p-8">
          {/* Action Buttons */}
          <div className="flex justify-between items-center mb-8">
            <div className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-gray-400" />
              <h3 className="text-lg font-semibold text-gray-900">Account Information</h3>
            </div>
            
            {!isEditing ? (
              <button 
                onClick={() => setIsEditing(true)}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-xl hover:from-indigo-600 hover:to-purple-600 transition-all duration-200 transform hover:scale-105 shadow-lg"
              >
                <Edit3 size={18} />
                Edit Profile
              </button>
            ) : (
              <div className="flex gap-2">
                <button 
                  onClick={handleCancel}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors"
                >
                  <X size={18} />
                  Cancel
                </button>
                <button 
                  onClick={handleSave}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl hover:from-green-600 hover:to-emerald-600 transition-all duration-200 transform hover:scale-105 shadow-lg"
                >
                  <Save size={18} />
                  Save Changes
                </button>
              </div>
            )}
          </div>

          {/* Save Status */}
          {saveStatus === 'saved' && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-2 text-green-800">
              <Check className="w-5 h-5" />
              Profile updated successfully!
            </div>
          )}

          {/* Profile Fields */}
          <div className="space-y-6">
            {/* User ID */}
            <div className="group">
              <label className="block text-sm font-medium text-gray-700 mb-2">User ID</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Shield className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  name="userId"
                  value={editedUser.userId}
                  onChange={handleInputChange}
                  disabled
                  className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-500 cursor-not-allowed focus:outline-none"
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">User ID cannot be changed</p>
            </div>

            {/* Username */}
            <div className="group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Username</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-gray-400" />
                </div>
                {isEditing ? (
                  <input
                    type="text"
                    name="username"
                    value={editedUser.username || ''}
                    onChange={handleInputChange}
                    className="w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 hover:border-gray-300"
                    placeholder="Enter your username"
                  />
                ) : (
                  <div className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900">
                    {user.username || 'Not set'}
                  </div>
                )}
              </div>
            </div>

            {/* Email */}
            <div className="group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                {isEditing ? (
                  <input
                    type="email"
                    name="email"
                    value={editedUser.email || ''}
                    onChange={handleInputChange}
                    className="w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 hover:border-gray-300"
                    placeholder="Enter your email"
                  />
                ) : (
                  <div className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900">
                    {user.email || 'Not set'}
                  </div>
                )}
              </div>
            </div>

            {/* Additional Profile Stats */}
            <div className="mt-8 grid grid-cols-2 gap-4">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-xl border border-blue-100">
                <div className="text-2xl font-bold text-blue-600">24</div>
                <div className="text-sm text-blue-700">Profile Views</div>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-4 rounded-xl border border-purple-100">
                <div className="text-2xl font-bold text-purple-600">12</div>
                <div className="text-sm text-purple-700">Connections</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const { user: authUser, dispatch } = useContext(AuthContext);
  const [user, setUser] = useState(null);
  const [storageAvailable, setStorageAvailable] = useState(false);

  const checkStorageAvailability = () => {
    try {
      const test = 'test';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch (e) {
      return false;
    }
  };

  useEffect(() => {
    const isStorageAvailable = checkStorageAvailability();
    setStorageAvailable(isStorageAvailable);

    try {
      if (isStorageAvailable && authUser) {
        // Use the user data from AuthContext which includes profile data
        setUser(authUser);
        
        // Also store it in localStorage for persistence
        const profileDataKey = `userProfile_${authUser.userId || authUser.id}`;
        localStorage.setItem(profileDataKey, JSON.stringify(authUser));
      } else if (!authUser) {
        // Fallback for demo purposes
        const demoUser = {
          userId: "usr_" + Math.random().toString(36).substr(2, 9),
          username: "demo_user",
          email: "demo@example.com",
          profileImage: null
        };
        setUser(demoUser);
      }
    } catch (err) {
      console.error("Error parsing user data:", err);
      setUser(null);
    }
  }, [authUser]); // Add authUser as dependency

  const handleUpdateUser = (updatedUser) => {
    setUser(updatedUser);
    
    try {
      if (storageAvailable) {
        // Update AuthContext with the new profile data
        dispatch({
          type: "UPDATE_USER_PROFILE",
          payload: updatedUser
        });

        // Also store in localStorage
        const profileDataKey = `userProfile_${updatedUser.userId || updatedUser.id}`;
        localStorage.setItem(profileDataKey, JSON.stringify(updatedUser));
        console.log("User profile updated:", updatedUser);
      } else {
        console.log("User profile updated in memory:", updatedUser);
      }
    } catch (err) {
      console.error("Error saving user profile:", err);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      <UserPageHeader />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mt-14"></div>  
        <UserProfile user={user} onUpdate={handleUpdateUser} />
      </div>
    </div>
  );
}